import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getWaitingApprovals } from '../services/humanComputerApi';
import UserStreamManager from '../utils/UserStreamManager';
import { useAuth } from './AuthContext';

/**
 * ApprovalStampContext (Nitish, Oct 9 2026, CEL-3095): the approval cards waiting for the signed-in person, so the
 * sidebar can stamp the employee or chat row while the owner works elsewhere. The SERVER decides who sees a stamp
 * (the human who messaged in that run, the same rule as sound and push); this context only mirrors
 * GET /human-computer/waiting. Refreshed on the approval events, on a chat stopping, and every minute as a backstop
 * (an employee card that times out resolves through the sweep, which publishes HUMAN_COMPUTER_BATCH_UPDATED).
 */
const EMPTY = { byChat: {}, byEmployee: {}, windowMs: 0 };
const ApprovalStampContext = createContext(EMPTY);
const APPROVAL_EVENTS = ['HUMAN_COMPUTER_COMMANDS_PENDING', 'HUMAN_COMPUTER_BATCH_UPDATED', 'HUMAN_COMPUTER_COMMANDS_APPROVED'];
const POLL_MS = 60000;
const DEBOUNCE_MS = 500;

export const ApprovalStampProvider = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [state, setState] = useState({ rows: [], windowMs: 0 });
  const debounceRef = useRef(null);
  const joined = !!currentUser && userProfile?.onboarding_status === 'JOINED';

  const refresh = useCallback(async () => {
    try {
      const res = await getWaitingApprovals();
      setState({ rows: res.data?.waiting || [], windowMs: (res.data?.window_seconds || 0) * 1000 });
    } catch (e) {
      // Keep the last known stamps; the next event or poll corrects them.
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refresh, DEBOUNCE_MS);
  }, [refresh]);

  useEffect(() => {
    if (!joined) {
      setState({ rows: [], windowMs: 0 });
      return undefined;
    }
    refresh();
    const unsubs = APPROVAL_EVENTS.map((type) => UserStreamManager.subscribe(type, scheduleRefresh));
    // A chat stopping (or pausing on a card) changes what waits; ignore every other status update.
    unsubs.push(UserStreamManager.subscribe('CHAT_STATUS_UPDATE', (data) => {
      if (data && (data.operating === false || data.human_computer_turn !== undefined)) scheduleRefresh();
    }));
    const poll = setInterval(refresh, POLL_MS);
    return () => {
      unsubs.forEach((u) => typeof u === 'function' && u());
      clearInterval(poll);
      clearTimeout(debounceRef.current);
    };
  }, [joined, refresh, scheduleRefresh]);

  const value = useMemo(() => {
    const byChat = {};
    const byEmployee = {};
    for (const row of state.rows) {
      if (row.employee_id) byEmployee[row.employee_id] = row;
      else byChat[row.chat_id] = row;
    }
    return { byChat, byEmployee, windowMs: state.windowMs };
  }, [state]);

  return <ApprovalStampContext.Provider value={value}>{children}</ApprovalStampContext.Provider>;
};

export const useApprovalStamps = () => useContext(ApprovalStampContext);

export default ApprovalStampContext;
